import { useState, useEffect, useCallback } from 'react';
import { knowledgeItems } from '../../mocks/knowledge';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { adminStore, DEFAULT_VIDEOS } from '../../store/adminStore';

const TABS = ['Tất cả', 'Video', 'Hình ảnh'];

const DEFAULT_VIDEO_ITEMS = DEFAULT_VIDEOS;

function extractYoutubeId(input: string): string {
  const trimmed = input.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  const shortMatch = trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortMatch) return shortMatch[1];
  const watchMatch = trimmed.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1];
  const embedMatch = trimmed.match(/embed\/([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return embedMatch[1];
  return trimmed;
}

interface VideoItem {
  id: string;
  title: string;
  youtubeId?: string;
  localUrl?: string;
  type: 'youtube' | 'local';
  description?: string;
}

function useVideoItems() {
  const [items, setItems] = useState<VideoItem[]>(() => adminStore.getStore().videos);

  useEffect(() => {
    const handler = () => setItems(adminStore.getStore().videos);
    window.addEventListener('bahnar-admin-updated', handler);
    return () => window.removeEventListener('bahnar-admin-updated', handler);
  }, []);

  return { items };
}

// ─── VideoCard ────────────────────────────────────────────────────────────────
interface LightboxVideoItem { type: 'video'; id: string; title: string; youtubeId: string; }
interface LightboxImageItem { type: 'image'; id: string; title: string; image: string; }
type LightboxItem = LightboxVideoItem | LightboxImageItem;

function Lightbox({
  item,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}: {
  item: LightboxItem;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-xl transition-all cursor-pointer z-10"
      >
        <i className="ri-close-line text-white text-2xl"></i>
      </button>

      {/* Prev */}
      {hasPrev && (
        <button
          onClick={e => { e.stopPropagation(); onPrev(); }}
          className="absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-xl transition-all cursor-pointer z-10"
        >
          <i className="ri-arrow-left-line text-white text-2xl"></i>
        </button>
      )}

      {/* Next */}
      {hasNext && (
        <button
          onClick={e => { e.stopPropagation(); onNext(); }}
          className="absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-xl transition-all cursor-pointer z-10"
        >
          <i className="ri-arrow-right-line text-white text-2xl"></i>
        </button>
      )}

      {/* Content */}
      <div
        className="relative w-full mx-16 max-w-5xl"
        onClick={e => e.stopPropagation()}
      >
        {item.type === 'video' ? (
          <div className="rounded-2xl overflow-hidden" style={{ paddingTop: '56.25%', position: 'relative' }}>
            <iframe
              src={`https://www.youtube.com/embed/${item.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
              title={item.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>
        ) : (
          <img
            src={item.image}
            alt={item.title}
            className="w-full max-h-[80vh] object-contain rounded-2xl"
          />
        )}
        <p className="mt-4 text-center text-white/90 font-semibold text-lg">{item.title}</p>
      </div>
    </div>
  );
}

type MailboxStatus = 'idle' | 'submitting' | 'success' | 'error';

function MailboxFAB() {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mediaType, setMediaType] = useState('Hình ảnh');
  const [description, setDescription] = useState('');
  const [link, setLink] = useState('');
  const [status, setStatus] = useState<MailboxStatus>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !description.trim()) return;
    if (description.length > 1000) return;
    setStatus('submitting');
    try {
      const body = new URLSearchParams();
      body.append('name', name);
      body.append('email', email);
      body.append('phone', phone);
      body.append('mediaType', mediaType);
      body.append('description', description);
      body.append('link', link);
      const res = await fetch('https://readdy.ai/api/form/d729r7kij602egd95e5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
      if (res.ok) {
        setStatus('success');
        setName(''); setEmail(''); setPhone('');
        setDescription(''); setLink(''); setMediaType('Hình ảnh');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (status === 'success') setStatus('idle');
  };

  return (
    <>
      {/* FAB Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-8 right-8 z-50 w-20 h-20 flex flex-col items-center justify-center rounded-2xl cursor-pointer transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
        style={{
          background: 'linear-gradient(135deg, #d4521a, #f5b942)',
          boxShadow: '0 6px 24px rgba(180,80,0,0.45), 0 2px 8px rgba(180,80,0,0.3)',
        }}
        title="Hộp thư gửi tư liệu"
      >
        <i className="ri-mail-send-fill text-white text-3xl"></i>
        <span className="text-white text-[10px] font-bold mt-1 leading-none">Hộp thư</span>
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-end p-6"
          style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)' }}
          onClick={handleClose}
        >
          <div
            className="w-full max-w-lg bg-white rounded-3xl overflow-hidden"
            style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.25)', maxHeight: '90vh' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div
              className="px-7 py-5 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg, #d4521a, #f5b942)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-mail-send-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-white">Hộp thư gửi tư liệu</h3>
                  <p className="text-xs text-white/80">Gửi video / hình ảnh đặc sắc đến nhà sáng lập</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/35 rounded-xl transition-all cursor-pointer"
              >
                <i className="ri-close-line text-white text-xl"></i>
              </button>
            </div>

            {/* Modal body */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 88px)' }}>
              <div className="px-7 py-6">
                {status === 'success' ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                      <i className="ri-check-double-fill text-3xl text-green-500"></i>
                    </div>
                    <h3 className="text-xl font-bold text-primary-brown-dark mb-2">Gửi thành công!</h3>
                    <p className="text-primary-brown-light mb-6 text-sm">Cảm ơn bạn đã đóng góp tư liệu quý giá. Chúng tôi sẽ xem xét và liên hệ sớm nhất có thể.</p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="px-8 py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-full hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Gửi thêm tư liệu khác
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <p className="text-sm text-primary-brown-light mb-2">
                      Bạn có video hay hình ảnh đặc sắc về văn hóa Bahnar? Hãy gửi về cho chúng tôi nhé!
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-primary-brown-dark mb-1.5">
                          Họ tên <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={name}
                          onChange={e => setName(e.target.value)}
                          placeholder="Nguyễn Văn A"
                          required
                          className="w-full px-3 py-2.5 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-primary-brown-dark mb-1.5">
                          Email <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          placeholder="abc@gmail.com"
                          required
                          className="w-full px-3 py-2.5 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-primary-brown-dark mb-1.5">Số điện thoại</label>
                        <input
                          type="text"
                          name="phone"
                          value={phone}
                          onChange={e => setPhone(e.target.value)}
                          placeholder="0900000000"
                          className="w-full px-3 py-2.5 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-primary-brown-dark mb-1.5">Loại tư liệu</label>
                        <select
                          name="mediaType"
                          value={mediaType}
                          onChange={e => setMediaType(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark focus:outline-none focus:border-primary-orange transition-colors cursor-pointer"
                        >
                          <option>Hình ảnh</option>
                          <option>Video</option>
                          <option>Cả hai</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-primary-brown-dark mb-1.5">
                        Link tư liệu (Drive, YouTube...)
                      </label>
                      <input
                        type="text"
                        name="link"
                        value={link}
                        onChange={e => setLink(e.target.value)}
                        placeholder="https://drive.google.com/..."
                        className="w-full px-3 py-2.5 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-primary-brown-dark mb-1.5">
                        Mô tả nội dung <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        name="description"
                        value={description}
                        onChange={e => setDescription(e.target.value.slice(0, 1000))}
                        required
                        rows={3}
                        placeholder="Mô tả ngắn gọn về video/hình ảnh, hoàn cảnh ghi lại, địa điểm..."
                        className="w-full px-3 py-2.5 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors resize-none"
                      />
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-primary-brown-light">{description.length}/1000 ký tự</span>
                        {description.length > 1000 && <span className="text-xs text-red-400">Vượt quá 1000 ký tự</span>}
                      </div>
                    </div>

                    {status === 'error' && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl">
                        <i className="ri-error-warning-line text-red-400"></i>
                        <p className="text-sm text-red-500">Có lỗi xảy ra, vui lòng thử lại.</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'submitting' || !name.trim() || !email.trim() || !description.trim() || description.length > 1000}
                      className="w-full py-3.5 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 flex items-center justify-center gap-2 text-sm"
                    >
                      {status === 'submitting' ? (
                        <><i className="ri-loader-4-line animate-spin"></i> Đang gửi...</>
                      ) : (
                        <><i className="ri-send-plane-fill"></i> Gửi tư liệu đến nhà sáng lập</>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── VideoCard ────────────────────────────────────────────────────────────────
function VideoCard({ item, onExpand }: { item: VideoItem; onExpand: () => void }) {
  const [playing, setPlaying] = useState(false);

  const isLocal = item.type === 'local';
  const localSrc = item.localUrl ?? '';

  return (
    <div className="bg-white rounded-2xl overflow-hidden border-2 border-primary-orange/15 hover:border-primary-orange/45 transition-all group">
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        {playing ? (
          isLocal ? (
            <video
              src={localSrc}
              controls
              autoPlay
              className="absolute inset-0 w-full h-full object-contain bg-black"
            />
          ) : (
            <iframe
              src={`https://www.youtube.com/embed/${item.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
              title={item.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          )
        ) : (
          <>
            {isLocal ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-orange-50 to-yellow-50">
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-2">
                    <i className="ri-file-video-fill text-orange-400 text-3xl"></i>
                  </div>
                  <p className="text-xs text-orange-400 font-medium">Video đã tải lên</p>
                </div>
              </div>
            ) : (
              <img
                src={`https://img.youtube.com/vi/${item.youtubeId}/hqdefault.jpg`}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div
              className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/35 transition-all cursor-pointer"
              onClick={() => setPlaying(true)}
            >
              <div className={`w-16 h-16 flex items-center justify-center rounded-full group-hover:scale-110 transition-transform shadow-lg ${isLocal ? 'bg-orange-500' : 'bg-red-600'}`}>
                <i className="ri-play-fill text-white text-3xl ml-1"></i>
              </div>
            </div>
            {!isLocal && (
              <button
                onClick={onExpand}
                className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-black/70 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                title="Xem toàn màn hình"
              >
                <i className="ri-fullscreen-line text-white text-sm"></i>
              </button>
            )}
          </>
        )}
      </div>
      <div className="p-4 flex items-center justify-between gap-3">
        <h3 className="font-bold text-primary-brown-dark text-base group-hover:text-primary-orange transition-colors flex-1">{item.title}</h3>
        {playing && (
          <button
            onClick={() => setPlaying(false)}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            title="Dừng video"
          >
            <i className="ri-stop-fill text-gray-500 text-sm"></i>
          </button>
        )}
      </div>
    </div>
  );
}

export default function KnowledgePage() {
  const [activeTab, setActiveTab] = useState('Tất cả');
  const { items: videoItems } = useVideoItems();

  // Lightbox state
  const [lightboxItems, setLightboxItems] = useState<LightboxItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = useCallback((items: LightboxItem[], index: number) => {
    setLightboxItems(items);
    setLightboxIndex(index);
  }, []);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevLightbox = useCallback(() => setLightboxIndex(i => (i !== null && i > 0 ? i - 1 : i)), []);
  const nextLightbox = useCallback(() => setLightboxIndex(i => (i !== null && i < lightboxItems.length - 1 ? i + 1 : i)), [lightboxItems.length]);

  const videoLightboxItems: LightboxItem[] = videoItems
    .filter(v => v.type === 'youtube' && v.youtubeId)
    .map(v => ({
      type: 'video' as const,
      id: v.id,
      title: v.title,
      youtubeId: v.youtubeId!,
    }));

  const imageLightboxItems: LightboxItem[] = knowledgeItems.map(item => ({
    type: 'image',
    id: String(item.id),
    title: item.title,
    image: item.image,
  }));

  return (
    <div className="min-h-screen bg-primary-cream">
      <Header />

      {/* Hero */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Bahnar%20ethnic%20cultural%20artifacts%20and%20knowledge%20items%20arranged%20beautifully%2C%20traditional%20woven%20fabrics%20geometric%20patterns%2C%20bamboo%20instruments%2C%20handcrafted%20items%20on%20wooden%20surface%2C%20warm%20golden%20studio%20light%2C%20soft%20natural%20bokeh%2C%20earthy%20orange%20and%20brown%20color%20tones%2C%20cultural%20heritage%20Vietnam%2C%20artistic%20flat%20lay%20composition&width=1400&height=400&seq=knowledge-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
            style={{ filter: 'blur(3px) brightness(0.65)', transform: 'scale(1.05)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-primary-cream/95"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-yellow/30 rounded-full mb-4 border border-primary-yellow/50">
                <i className="ri-seedling-fill text-primary-yellow"></i>
                <span className="text-sm font-bold text-white uppercase tracking-wider">Hạt giống tri thức</span>
              </div>
              <h1
                className="text-6xl font-extrabold text-white mb-4 leading-tight"
                style={{ textShadow: '0 2px 4px rgba(0,0,0,0.6), 0 4px 20px rgba(0,0,0,0.5), 0 0 40px rgba(180,80,0,0.3)', letterSpacing: '-0.5px' }}
              >
                Kho tàng văn hóa<br />Bahnar
              </h1>
              <p className="text-xl text-white/85 mb-8" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}>
                Video, hình ảnh về văn hóa và cuộc sống của người Bahnar.
              </p>
            </div>
            <div>
              <img
                src="https://static.readdy.ai/image/1cc4dff2fab325f8b161c01cf2cdaccb/c2dc1a26fdd090d3133f2e929e9b6300.jpeg"
                alt="Hạt giống tri thức"
                className="w-full h-80 object-cover object-top rounded-3xl border-4 border-white/30 ring-4 ring-primary-orange/25"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tab Filter */}
      <section className="sticky top-16 z-30 bg-primary-orange/10 backdrop-blur-sm border-b border-primary-orange/20 py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center gap-3">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab
                  ? 'bg-primary-orange text-white'
                  : 'bg-white text-primary-brown-light hover:text-primary-orange border border-primary-brown-light/20'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </section>

      {/* Video Section */}
      {(activeTab === 'Tất cả' || activeTab === 'Video') && (
        <section className="py-14">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-primary-brown-dark flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl">
                  <i className="ri-youtube-fill text-red-500 text-xl"></i>
                </div>
                Video
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {videoItems.map((item) => {
                const ytIdx = videoLightboxItems.findIndex(l => l.id === item.id);
                return (
                  <VideoCard
                    key={item.id}
                    item={item}
                    onExpand={() => ytIdx >= 0 ? openLightbox(videoLightboxItems, ytIdx) : undefined}
                  />
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Culture Facts / Image Section */}
      {(activeTab === 'Tất cả' || activeTab === 'Hình ảnh') && (
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-primary-brown-dark mb-8 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-orange/10 rounded-xl">
                <i className="ri-gallery-fill text-primary-orange text-xl"></i>
              </div>
              Hình ảnh
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {knowledgeItems.map((item, idx) => (
                <div
                  key={item.id}
                  className="bg-primary-cream rounded-2xl overflow-hidden transition-all cursor-pointer border-2 border-primary-orange/15 hover:border-primary-orange/45 group"
                  onClick={() => openLightbox(imageLightboxItems, idx)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Expand overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-all">
                      <div className="w-12 h-12 flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100">
                        <i className="ri-zoom-in-line text-primary-brown-dark text-xl"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-primary-brown-dark text-base group-hover:text-primary-orange transition-colors">{item.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      {/* Floating Mailbox FAB */}
      <MailboxFAB />

      {/* Lightbox */}
      {lightboxIndex !== null && lightboxItems.length > 0 && (
        <Lightbox
          item={lightboxItems[lightboxIndex]}
          onClose={closeLightbox}
          onPrev={prevLightbox}
          onNext={nextLightbox}
          hasPrev={lightboxIndex > 0}
          hasNext={lightboxIndex < lightboxItems.length - 1}
        />
      )}
    </div>
  );
}
